import React, { forwardRef, useRef, useContext, useMemo } from "react";
import { DateTime } from "luxon";
import {
  Identifiable,
  OptimizedGantt,
  Project,
  ProjectElementType,
  resource,
  WorkLoadsCalendar,
} from "@planzone/dto";
import { useRetrieveWbsMeta } from "@planzone/view-models";

import { filter as filterData } from "@common/filter";
import { sort as sortData } from "@common/sort";
import { Context } from "../context";
import { ResourceCtx } from "../wbs/resourceCtx";
import { ProjectCtx } from "../projectCtx";
import { Calendar } from "./calendar";
import { Element } from "./elementsOverlay/element";
import { Views } from "../api/views";
import { renderWithTreeCtx } from "../utils";
import { Props } from "./types";
import styles from "./styles.scss";
import { useUtils } from "@common/treeToggle";

const Graph = forwardRef<HTMLDivElement, Props>(
  (
    { workLoads, projects, dates, filter, sort, constraints, zone },
    forwardedGraphRef: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const userTimezone = DateTime.local().zoneName;
    const ref = useRef<HTMLDivElement>(null);
    const { cell, currentView, elementsNbrMap } = useContext(Context);
    const { isOpen } = useUtils();
    const resourceOffsets = useRef<{ [key: string]: number }>({});

    const { meta: wbsMeta } = useRetrieveWbsMeta(projects);

    const filteredWorkLoads = useMemo(
      () =>
        sortData(
          sort,
          filterData(workLoads, filter, (workLoad) => ({
            resource: {
              ...resource,
              uuid: workLoad.uuid,
              name: workLoad.resourceName,
              tags: workLoad.resourceTags.map((name) => ({ name })),
            },
          })),
          (workLoad) => ({ name: workLoad.resourceName }),
        ),
      [workLoads, filter, sort],
    );

    const minDate = dates[0] || DateTime.now().setZone(userTimezone);
    const paddingTop = useMemo(
      () =>
        cell.height +
        1 +
        (currentView === Views.DAILY
          ? cell.height * 2
          : cell.height + cell.height * (1 / 3)),
      [cell.height, currentView],
    );

    const calculateVisibleElementsCount = (
      projectsForResource: Identifiable<Project>[],
      workLoad: WorkLoadsCalendar[number],
    ) => {
      let visibleCount = 1;

      projectsForResource.forEach((project) => {
        const isProjectOpen = isOpen(`${workLoad.uuid}:${project.uuid}`);
        const projectElements = elementsNbrMap
          ? elementsNbrMap[`${workLoad.uuid}:${project.uuid}`]
          : 0;

        projectsForResource.length > 1
          ? (visibleCount += isProjectOpen ? projectElements : 1)
          : (visibleCount += projectElements);
      });

      return visibleCount;
    };

    const renderResourceElements = (
      workLoad: WorkLoadsCalendar[number],
      resourceOffset: number,
    ) => (
      <ResourceCtx resource={workLoad} zone={zone} sort={sort}>
        {(projects, isLoadingResource) => {
          if (isLoadingResource) {
            const projectsForResource = filterData(
              projects,
              filter,
              (project) => ({ project }),
            );
            const visibleCount = calculateVisibleElementsCount(
              projectsForResource,
              workLoad,
            );
            resourceOffsets.current[workLoad.uuid] =
              resourceOffset + visibleCount;

            return null;
          }

          const projectsForResource = filterData(
            projects,
            filter,
            (project) => ({ project }),
          );
          let currentOffset = resourceOffset + 1;

          const elementsFragment = (
            <>
              <div className={styles.elementsOverlay}>
                {projectsForResource.map((project, projectIndex) => (
                  <ProjectCtx
                    key={`${project.uuid}`}
                    workLoad={workLoad}
                    project={project}
                    filter={filter}
                    sort={sort}
                    isMultiProject={projectsForResource.length > 1}
                    isFlagRendered={false}
                  >
                    {(ganttByProjectData, isLoadingProject) => {
                      if (isLoadingProject) return null;

                      const elementsInBoth = ganttByProjectData;

                      const projectFragment = (
                        <>
                          {renderWithTreeCtx(
                            elementsInBoth,
                            isOpen,
                            workLoad,
                            (projectElement, ctx, index) => {
                              const elementConstraints = constraints.filter(
                                ({ targetEntityUuid, originEntityUuid }) =>
                                  originEntityUuid === projectElement.uuid ||
                                  targetEntityUuid === projectElement.uuid,
                              );

                              const elMeta = wbsMeta[projectElement.uuid] || {
                                allocatedHours: 0,
                                attachmentCount: 0,
                                commentCount: 0,
                                elementCount: 0,
                                duration: 0,
                                percentComplete: 0,
                                spentHours: 0,
                                uuid: projectElement.uuid,
                              };

                              const elementTop =
                                cell.height * (currentOffset + index) +
                                paddingTop +
                                45;
                              const elementZIndex =
                                elementsInBoth.length +
                                currentOffset -
                                (index + projectIndex);

                              return (
                                <div
                                  id={`resourceElement:${workLoad.uuid}:${projectElement.uuid}`}
                                  key={`${workLoad.uuid}-${projectElement.uuid}`}
                                  className={styles.element}
                                  style={{
                                    top: elementTop,
                                    height: cell.height,
                                    position: "absolute",
                                    zIndex: elementsInBoth.length - index + 100,
                                  }}
                                >
                                  <Element
                                    element={projectElement}
                                    index={index}
                                    ctx={ctx}
                                    graphRef={ref}
                                    dates={dates}
                                    project={project}
                                    constraints={elementConstraints}
                                    meta={elMeta}
                                    minDate={minDate}
                                  />
                                </div>
                              );
                            },
                          )}
                        </>
                      );

                      currentOffset += elementsInBoth.length;

                      return projectFragment;
                    }}
                  </ProjectCtx>
                ))}
              </div>
            </>
          );

          const totalVisibleElements = calculateVisibleElementsCount(
            projectsForResource,
            workLoad,
          );
          resourceOffsets.current[workLoad.uuid] =
            resourceOffset + totalVisibleElements;

          return elementsFragment;
        }}
      </ResourceCtx>
    );

    return (
      <div ref={forwardedGraphRef} className={styles.graph}>
        <div
          ref={ref}
          className={styles.main}
          style={{ width: cell.width * dates.length }}
        >
          <div className={styles.elementsOverlay}>
            {filteredWorkLoads.map((workLoad, resourceIndex) => {
              const resourceOffset = filteredWorkLoads[resourceIndex - 1]
                ? resourceOffsets.current[
                    filteredWorkLoads[resourceIndex - 1].uuid
                  ] || 0
                : 0;

              return (
                <React.Fragment key={workLoad.uuid}>
                  <div
                    className={styles.resourceWorkloadBar}
                    style={{
                      position: "absolute",
                      top: resourceOffset * cell.height + paddingTop + 45,
                      height: cell.height,
                      width: "100%",
                    }}
                  >
                    <Calendar dates={dates} workLoads={[workLoad]} />
                  </div>

                  {isOpen(workLoad.uuid) ? (
                    renderResourceElements(workLoad, resourceOffset)
                  ) : (
                    <>
                      {(() => {
                        resourceOffsets.current[workLoad.uuid] =
                          resourceOffset + 1;
                        return null;
                      })()}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  },
);

export { Graph };
